import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  BookOpen,
  ArrowRight,
  Sparkles,
  Star,
  PlayCircle,
  Gift,
  Download,
  X,
  CheckCircle2,
} from 'lucide-react';
import { resourceData } from '../mock';

const Resources = () => {
  const { book, courses, digitalFiles, freeChallenge } = resourceData;
  const fridgeMagic = courses.find((c) => c.id === 'fridge-magic');
  const otherCourses = courses.filter((c) => c.id !== 'fridge-magic');

  return (
    <div className="resources-page storefront" data-testid="resources-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/" data-testid="logo-link">
            <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/carebnb" className="nav-link" data-testid="resources-nav-carebnb">CareBnB</Link>
            <Link to="/resources" className="nav-link" data-testid="nav-resources">Resources</Link>
          </nav>
          <div className="nav-actions">
            <a
              href="https://wa.me/972529326458"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-contact"
              data-testid="header-contact-btn"
            >
              <Phone size={16} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </header>

      {/* Boutique Hero */}
      <section className="storefront-hero" data-testid="storefront-hero">
        <div className="container">
          <span className="storefront-eyebrow">
            <Sparkles size={14} /> The Momshiach Library
          </span>
          <h1 className="heading-hero" data-testid="resources-hero-title">
            Come home to your kitchen —<br />
            <em>and to yourself.</em>
          </h1>
          <p className="storefront-lede body-large">
            The cookbook, courses, workbooks, and community that help you show up for
            your own health — one small, doable habit at a time.
          </p>
        </div>
      </section>

      {/* Start Free Hook */}
      <section className="start-free-strip" data-testid="start-free-strip">
        <div className="container start-free-inner">
          <div className="start-free-copy">
            <span className="start-free-tag">
              <Gift size={14} /> Start Free
            </span>
            <h2 className="heading-2">7 Days to a Softer Rhythm</h2>
            <p className="body-medium">
              Try one gentle habit a day, straight to your inbox. No pressure, no perfection —
              just a warm hand on your shoulder. The best place to start if you&apos;re new here.
            </p>
          </div>
          <a
            href={freeChallenge.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary btn-lg start-free-cta"
            data-testid="free-challenge-cta"
          >
            <span>{freeChallenge.cta}</span>
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* The Cookbook — Editorial Long-Form Feature */}
      <section className="book-editorial" data-testid="book-feature">
        {/* Hero */}
        <div className="container">
          <div className="book-editorial-hero">
            <div className="book-cover-wrap">
              <div className="book-cover-glow" />
              <img
                src={book.coverUrl}
                alt={`${book.title} cover`}
                className="book-cover-img"
                data-testid="book-cover-img"
                loading="lazy"
              />
              <div className="book-cover-badge">
                <Star size={12} fill="currentColor" /> The Cookbook
              </div>
            </div>
            <div className="book-editorial-copy">
              <span className="storefront-eyebrow">
                <BookOpen size={14} /> The Signature Cookbook
              </span>
              <h2 className="heading-hero book-title" data-testid="book-title">
                {book.title}
              </h2>
              <p className="book-subtitle">{book.subtitle}</p>
              <p className="book-coach-credit">— {book.coachCredit}</p>
              <div className="book-cta-row">
                <a
                  href={book.buyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-lg"
                  data-testid="book-buy-btn"
                >
                  <BookOpen size={18} />
                  <span>{book.buyCta}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* The Story */}
        <div className="container book-narrow">
          <div className="book-story" data-testid="book-story">
            {book.story.map((line, i) => (
              <p key={i} className="book-story-line" style={{ animationDelay: `${i * 0.08}s` }}>
                {line}
              </p>
            ))}
          </div>

          <div className="book-pull-quote" data-testid="book-pull-quote">
            <Sparkles size={16} />
            <p>{book.pullQuote}</p>
          </div>

          {/* The Approach */}
          <div className="book-approach" data-testid="book-approach">
            <p className="book-approach-lead">Not through:</p>
            <ul className="book-approach-not">
              {book.approachNot.map((item, i) => (
                <li key={i}><X size={16} /> <span>{item}</span></li>
              ))}
            </ul>
            <p className="book-approach-yes">
              <Sparkles size={18} /> {book.approachYes}
            </p>
          </div>

          {/* Coach intro */}
          <div className="book-coach-intro" data-testid="book-coach-intro">
            <p>{book.coachIntro}</p>
            <p>{book.coachIntro2}</p>
          </div>

          {/* Whether you are */}
          <div className="book-personas" data-testid="book-personas">
            <h3 className="heading-2 book-section-heading">Whether you are…</h3>
            <div className="book-personas-grid">
              {book.personas.map((p, i) => (
                <div key={i} className="book-persona-card">
                  <Sparkles size={14} />
                  <p>{p}</p>
                </div>
              ))}
            </div>
            <p className="book-personas-closing">{book.personasClosing}</p>
          </div>
        </div>

        {/* Why This Book Is Different */}
        <div className="container book-difference-wrap">
          <div className="book-difference" data-testid="book-difference">
            <span className="storefront-eyebrow"><Sparkles size={14} /> {book.differenceTitle}</span>
            <h3 className="heading-2 book-section-heading">
              This isn&apos;t just a collection of recipes.<br />
              <em>It&apos;s a foundation for a healthier lifestyle.</em>
            </h3>
            <p className="book-difference-intro">Inside, you&apos;ll learn:</p>
            <ul className="book-difference-list">
              {book.differenceItems.map((item, i) => (
                <li key={i}><CheckCircle2 size={18} /> <span>{item}</span></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Torah framing */}
        <div className="container book-narrow">
          <div className="book-torah-quote" data-testid="book-torah-quote">
            <Sparkles size={18} />
            <p className="book-torah-body">{book.torahQuote}</p>
            <p className="book-torah-closing">{book.torahClosing}</p>
          </div>
        </div>

        {/* Sample Pages */}
        <div className="container">
          <div className="storefront-section-header book-sample-header">
            <span className="storefront-eyebrow"><BookOpen size={14} /> Peek Inside</span>
            <h3 className="heading-1">Sample pages from the book</h3>
          </div>
          <div className="book-sample-grid" data-testid="book-sample-grid">
            {book.samplePages.map((page, i) => (
              <a
                key={page.id}
                href={`https://drive.google.com/file/d/${page.id}/view`}
                target="_blank"
                rel="noopener noreferrer"
                className="book-sample-card"
                data-testid={`book-sample-${i + 1}`}
              >
                <img
                  src={`https://drive.google.com/thumbnail?id=${page.id}&sz=w1000`}
                  alt={page.alt}
                  loading="lazy"
                />
                <span className="book-sample-hover">
                  <PlayCircle size={20} /> View full page
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Reader Reviews */}
        <div className="container">
          <div className="storefront-section-header">
            <span className="storefront-eyebrow"><Star size={14} /> Reader Reviews</span>
            <h3 className="heading-1">What moms are saying</h3>
          </div>
          <div className="book-testimonial-grid" data-testid="book-testimonials">
            {book.testimonials.map((t, i) => (
              <figure key={i} className="book-testimonial-card">
                <div className="book-testimonial-stars">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} size={14} fill="currentColor" />
                  ))}
                </div>
                <blockquote>{t.quote}</blockquote>
                <figcaption>— {t.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Companion Videos */}
        <div className="container book-narrow">
          <div className="book-companion-block" data-testid="book-companion-block">
            <span className="storefront-eyebrow"><PlayCircle size={14} /> Optional Add-On</span>
            <h3 className="heading-2 book-section-heading">{book.companion.title}</h3>
            <p>{book.companion.leadIn}</p>
            <p>{book.companion.desc}</p>
            <p className="book-companion-framing"><em>{book.companion.framing}</em></p>
            <a
              href={book.companion.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary btn-lg"
              data-testid="book-companion-btn"
            >
              <PlayCircle size={18} />
              <span>{book.companion.cta}</span>
            </a>
          </div>
        </div>

        {/* Closing */}
        <div className="container book-narrow">
          <div className="book-closing" data-testid="book-closing">
            {book.closing.map((line, i) => (
              <p key={i} className="book-closing-line">{line}</p>
            ))}
            <p className="book-closing-cta">{book.closingCta}</p>
            <a
              href={book.buyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-lg"
              data-testid="book-buy-btn-bottom"
            >
              <BookOpen size={18} />
              <span>{book.buyCta}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Featured Course — Fridge Magic */}
      {fridgeMagic && (
        <section className="featured-course" data-testid="featured-course">
          <div className="container">
            <div className="featured-course-card">
              <div className="featured-course-ribbon">
                <Sparkles size={12} /> Start Here
              </div>
              <div className="featured-course-body">
                <span className="storefront-eyebrow"><BookOpen size={14} /> Signature Course</span>
                <h2 className="heading-1">{fridgeMagic.title}</h2>
                <p className="featured-course-price">{fridgeMagic.price}</p>
                <p className="body-medium">{fridgeMagic.desc}</p>
                <ul className="featured-course-perks">
                  <li>Fridge audit + shopping map that actually saves you time</li>
                  <li>Meal prep foundations you&apos;ll come back to for years</li>
                  <li>Video walkthroughs at your own pace</li>
                  <li>Lifetime access to updates</li>
                </ul>
                <a
                  href={fridgeMagic.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary btn-lg"
                  data-testid="fridge-magic-cta"
                >
                  <span>{fridgeMagic.cta}</span>
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* The Library — courses + workbooks */}
      <section className="library-shelf" data-testid="library-shelf">
        <div className="container">
          <div className="storefront-section-header">
            <span className="storefront-eyebrow">
              <BookOpen size={14} /> More From The Library
            </span>
            <h2 className="heading-1">Take one more step in</h2>
            <p className="body-large text-secondary">
              Standalone courses, workbooks, and digital tools you can weave into your week.
            </p>
          </div>

          <div className="library-grid">
            {otherCourses.map((c) => (
              <a
                key={c.id}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="library-card"
                data-testid={`library-course-${c.id}`}
              >
                <span className="library-tag course"><BookOpen size={11} /> Course</span>
                <h3 className="heading-3">{c.title}</h3>
                {c.price && <div className="library-price">{c.price}</div>}
                <p className="body-small">{c.desc}</p>
                <span className="library-cta">
                  {c.cta} <ArrowRight size={14} />
                </span>
              </a>
            ))}
            {digitalFiles.map((f) => (
              <a
                key={f.id}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="library-card"
                data-testid={`library-digital-${f.id}`}
              >
                <span className="library-tag digital"><Download size={11} /> Digital</span>
                <h3 className="heading-3">{f.title}</h3>
                <p className="body-small">{f.desc}</p>
                <span className="library-cta">
                  {f.cta} <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="resources-final-cta">
        <div className="container">
          <h2 className="heading-1" data-testid="final-cta-heading">
            You don&apos;t need more pressure.<br />
            You need support that actually stays with you.
          </h2>
          <div className="cta-buttons">
            <Link to="/" className="btn-primary btn-lg" data-testid="final-work-with-me-btn">
              Work With Me 1:1
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="caption">
              © 2026 Momshiach. All rights reserved. Techniques taught under Rabbi Ben-Tzion Wosner shlit&quot;a.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
